import React, { useState } from "react";
import { TextField } from "@mui/material";
import { isAxiosError } from "axios";
import DSBox from "../../system-design/DSBox";
import DSButton from "../../system-design/DSButton";
import DSTypography from "../../system-design/DSTypography";
import useChangeSponsoredCodeMutation from "../hooks/useChangeSponsoredCodeMutation";

// Same rule the API enforces (COLLECTION_CODE_PATTERN in klydo-api); the API
// is the source of truth, this only saves an admin a round trip.
const CODE_PATTERN = /^[0-9A-Za-z!@#$%^&*()\-_=+]{6}$/;
const CODE_LENGTH = 6;

type Props = {
  collectionId: string;
  currentCode: string;
  disabled?: boolean;
};

const SponsoredCodeEditor: React.FC<Props> = ({
  collectionId,
  currentCode,
  disabled,
}) => {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const { mutate: changeCode, isPending } = useChangeSponsoredCodeMutation();

  const isValid = CODE_PATTERN.test(input);
  const canSave = isValid && input !== currentCode && !isPending && !disabled;

  const save = () => {
    setError("");
    changeCode(
      { collectionId, code: input },
      {
        onSuccess: () => setInput(""),
        onError: (e) =>
          setError(
            (isAxiosError(e) && e.response?.data?.message) ||
              "Failed to change the code. Please try again.",
          ),
      },
    );
  };

  return (
    <DSBox sx={{ mt: 1 }}>
      <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          size="small"
          label="New code"
          value={input}
          onChange={(e) => {
            setError("");
            setInput(e.target.value.toUpperCase().slice(0, CODE_LENGTH));
          }}
          disabled={disabled || isPending}
          inputProps={{ maxLength: CODE_LENGTH }}
          helperText={`${CODE_LENGTH} characters: letters, digits, or ! @ # $ % ^ & * ( ) - _ = +`}
        />
        <DSButton variant="outlined" disabled={!canSave} onClick={save}>
          Change
        </DSButton>
      </DSBox>
      {error && (
        <DSTypography variant="body2" color="error" sx={{ mt: 0.5 }}>
          {error}
        </DSTypography>
      )}
    </DSBox>
  );
};

export default SponsoredCodeEditor;
